import React, { useContext, useEffect, useState } from 'react'
import {
    Button,
    Card,
    CardBody,
    Col,
    CustomInput,
    Form,
    FormGroup,
    Input,
    Label,
    Row,
    Table
} from 'reactstrap'
import toastr from 'toastr'
import 'toastr/build/toastr.min.css'
import Select from 'react-select'
import ApiService from '../../../data/services/ApiService'
import { GeneralContext } from '../../../context/'

const emptyForm = {
    id: '',
    name: '',
    imageUrl: '',
    fileName: '',
    path: '',
    material: 'canvas',
    order: 1,
    isActive: true,
    url: ''
}

const ProductSampleImagesManager = () => {
    const { setLoading } = useContext(GeneralContext)
    const [images, setImages] = useState([])
    const [materials, setMaterials] = useState([])
    const [form, setForm] = useState(emptyForm)
    const [file, setFile] = useState()

    useEffect(() => {
        fetchImages()
        fetchMaterials()
    }, []) // eslint-disable-line

    const materialOptions = [
        { value: 'generic', label: 'General (todos los materiales)' },
        ...materials.map(material => ({
            value: material.code,
            label: material.name
        }))
    ]

    const getMaterialName = (code) => {
        if (code === 'generic') return 'General (todos los materiales)'
        return materials.find(material => material.code === code)?.name || code || 'Canvas'
    }

    const fetchImages = async () => {
        try {
            setLoading(true)
            const response = await ApiService.getProductSampleImages()
            const data = response.data.images || []
            setImages(data)
            setForm(current => ({
                ...current,
                order: current.id ? current.order : data.length + 1
            }))
        } catch (error) {
            toastr.error('No pude cargar las imágenes utilitarias', 'Error')
        } finally {
            setLoading(false)
        }
    }

    const fetchMaterials = async () => {
        try {
            const response = await ApiService.fetchMaterials()
            setMaterials(response.data.materials || [])
        } catch (error) {
            toastr.error('No pude cargar los materiales', 'Error')
        }
    }

    const resetForm = () => {
        setForm({
            ...emptyForm,
            order: images.length + 1
        })
        setFile()
    }

    const handleFileUpload = (event) => {
        const selectedFile = event.target.files[0]
        if (!selectedFile) { return }

        const types = ['image/png', 'image/jpeg', 'image/gif', 'image/webp']
        if (types.every(type => selectedFile.type !== type)) {
            toastr.error('Solo se aceptan imágenes', 'Error')
            return
        }

        setFile(selectedFile)
    }

    const saveImage = async (event) => {
        event.preventDefault()

        if (!form.name) {
            toastr.error('Agrega un nombre para la imagen', 'Error')
            return
        }

        if (!form.id && !file) {
            toastr.error('Selecciona una imagen', 'Error')
            return
        }

        try {
            setLoading(true)
            let payload = { ...form }

            if (file) {
                const data = new FormData()
                data.append('file', file)
                const upload = await ApiService.uploadProductSampleImage(data)

                payload = {
                    ...payload,
                    imageUrl: upload.data.imageUrl,
                    fileName: upload.data.fileName,
                    path: upload.data.path,
                    url: upload.data.url
                }
            }

            if (payload.id) {
                await ApiService.updateProductSampleImage(payload)
                toastr.success('Imagen actualizada', 'Éxito')
            } else {
                await ApiService.createProductSampleImage(payload)
                toastr.success('Imagen agregada', 'Éxito')
            }

            resetForm()
            await fetchImages()
        } catch (error) {
            toastr.error('No pude guardar la imagen', 'Error')
        } finally {
            setLoading(false)
        }
    }

    const editImage = (image) => {
        setForm({
            ...emptyForm,
            ...image
        })
        setFile()
    }

    const deleteImage = async (id) => {
        try {
            setLoading(true)
            await ApiService.deleteProductSampleImage(id)
            toastr.success('Imagen eliminada', 'Éxito')
            await fetchImages()
        } catch (error) {
            toastr.error('No pude eliminar la imagen', 'Error')
        } finally {
            setLoading(false)
        }
    }

    return (
        <Row>
            <Col md={4} xs={12}>
                <Card>
                    <CardBody>
                        <Form onSubmit={saveImage}>
                            <FormGroup>
                                <Label>Nombre *</Label>
                                <Input
                                    value={form.name}
                                    onChange={event => setForm({ ...form, name: event.target.value })}
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label>Orden</Label>
                                <Input
                                    type="number"
                                    value={form.order}
                                    onChange={event => setForm({ ...form, order: event.target.value })}
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label>Material *</Label>
                                <Select
                                    value={materialOptions.find(option => option.value === form.material) || null}
                                    options={materialOptions}
                                    classNamePrefix="select2-selection"
                                    placeholder="Seleccionar material"
                                    onChange={option => setForm({ ...form, material: option?.value || 'canvas' })}
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label>Imagen {form.id ? '' : '*'}</Label>
                                <Input type="file" onChange={handleFileUpload} />
                            </FormGroup>

                            {form.url && (
                                <FormGroup>
                                    <img alt={form.name} src={form.url} style={{ maxWidth: '100%' }} />
                                </FormGroup>
                            )}

                            <CustomInput
                                type="switch"
                                id="sample-image-active"
                                label="Activa"
                                checked={form.isActive}
                                onChange={() => setForm({ ...form, isActive: !form.isActive })}
                            />

                            <Button color="primary" className="mt-3" type="submit">
                                <i className="mdi mdi-check"></i> Guardar
                            </Button>

                            {form.id && (
                                <Button className="mt-3 ml-2" onClick={resetForm}>
                                    Cancelar
                                </Button>
                            )}
                        </Form>
                    </CardBody>
                </Card>
            </Col>

            <Col md={8} xs={12}>
                <Card>
                    <div className="table-responsive">
                        <Table striped className="align-middle">
                            <thead>
                                <tr>
                                    <th>Imagen</th>
                                    <th>Nombre</th>
                                    <th>Material</th>
                                    <th>Orden</th>
                                    <th>Activa</th>
                                    <th>...</th>
                                </tr>
                            </thead>
                            <tbody>
                                {images.map(image => (
                                    <tr key={image.id}>
                                        <td>
                                            <img alt={image.name} src={image.url} style={{ maxWidth: '120px' }} />
                                        </td>
                                        <td>{image.name}</td>
                                        <td>{getMaterialName(image.material || 'canvas')}</td>
                                        <td>{image.order}</td>
                                        <td>{image.isActive ? 'Sí' : 'No'}</td>
                                        <td>
                                            <Button size="sm" onClick={() => editImage(image)}>
                                                <i className="mdi mdi-pencil"></i>
                                            </Button>
                                            <Button size="sm" className="ml-2" onClick={() => deleteImage(image.id)}>
                                                <i className="mdi mdi-trash-can"></i>
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Card>
            </Col>
        </Row>
    )
}

export default ProductSampleImagesManager
